import { useEffect } from 'react';
import {
    AdSenseForShoppingPageOptions,
    AdSenseForShoppingAdBlock,
    Vertical,
    DeviceType,
} from '../../shared/types/ast';

import {
    bannerName,
    RESULT_LIST_SPACE_2,
    RESULT_LIST_SPACE_3,
    RESULT_LIST_SPACE_4,
} from '../utils/result';
import { SearchSummary } from '../../shared/types/adsense';

const SEARCH_RESULT_PAGES = 'partner-vert-pla-scm-finn-no-srp';
const CATEGORY_PAGES = 'partner-vert-pla-scm-finn-no-cat';
const GRID_VIEW_STYLE_ID = 6085245841;
const LIST_VIEW_STYLE_ID = 2950930037;
const ZERO_RESULT_CHANNEL = 'zsrp';

function createPageOption(
    page: number,
    query: string,
    fallback: string,
    channel: string,
    grid: boolean,
    zeroResults?: boolean,
): AdSenseForShoppingPageOptions {
    const combinedQuery = query || fallback;

    const styleId = grid ? GRID_VIEW_STYLE_ID : LIST_VIEW_STYLE_ID;

    return {
        pubId: query ? SEARCH_RESULT_PAGES : CATEGORY_PAGES,
        query: combinedQuery,
        channel: zeroResults ? ZERO_RESULT_CHANNEL : channel,
        adPage: page,
        styleId,
        adsafe: 'high',
        hl: 'no',
        linkTarget: '_blank',
    };
}

function createAdBlock(targetId: string) {
    const afshBlock: AdSenseForShoppingAdBlock = {
        container: targetId,
    };
    const element = document.getElementById(targetId);
    if (element) {
        const previousElement = element.previousElementSibling;

        if (previousElement && previousElement.clientHeight > 0) {
            afshBlock.height = previousElement.clientHeight;
            afshBlock.width = previousElement.clientWidth;
        }
    } else {
        console.error(
            `Unable to find DOM element ${targetId} during setup of AdSenseForShopping`,
        );
    }

    return afshBlock;
}

function clearAdBlock(targetId: string) {
    const element = document.getElementById(targetId);
    if (element) {
        element.innerHTML = '';
    }
}

export function useAdSense(
    children: React.ReactNode[],
    deviceType: DeviceType,
    vertical?: Vertical,
    config?: SearchSummary,
    grid?: boolean,
    zeroResults?: boolean,
) {
    const showAdSense = vertical === 'bap';
    const showAdSenseBoards = showAdSense && !!config?.query;

    const positions: string[] = [];

    if (showAdSenseBoards && children.length > RESULT_LIST_SPACE_2) {
        positions.push(bannerName(deviceType, 2));
    }

    if (showAdSenseBoards && children.length > RESULT_LIST_SPACE_3) {
        positions.push(bannerName(deviceType, 3));
    }

    if (showAdSenseBoards && children.length > RESULT_LIST_SPACE_4) {
        positions.push(bannerName(deviceType, 4));
    }

    positions.push('afshcontainer');

    const { channel = '', fallback = '', page = 1, query = '' } = config || {};
    const positionsCheck = positions.join();

    useEffect(() => {
        if (showAdSense) {
            try {
                if (typeof window._googCsa === 'function') {
                    const pageOptions = createPageOption(
                        page,
                        query,
                        fallback,
                        channel,
                        !!grid,
                        zeroResults,
                    );
                    const adBlockPositions = positions.map(createAdBlock);
                    if (pageOptions.query && adBlockPositions.length) {
                        window._googCsa('plas', pageOptions, adBlockPositions);
                    }
                }
            } catch (error) {
                console.error('Error in setup of AdSenseForShopping', error);
            }
        }
        return function cleanup() {
            if (showAdSense) {
                positions.forEach(clearAdBlock);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        positionsCheck, // if we use the positions array her it will update to often
        showAdSense,
        page,
        query,
        fallback,
        channel,
        grid,
        zeroResults,
    ]);

    return {
        showAdSense,
        showAdSenseBoards,
    };
}
